<div class="p-4 pt-0">
  <div class="page-heading mb-4">
    <h3 class="text-2xl font-semibold text-neutral-800" i18n>Live Sessions</h3>
  </div>

  <!-- Tabs -->
  <div class="flex border-b border-neutral-200 mb-4">
    <button
      (click)="switchTab('sessions')"
      class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
      [class.border-blue-600]="activeTab() === 'sessions'"
      [class.text-blue-600]="activeTab() === 'sessions'"
      [class.border-transparent]="activeTab() !== 'sessions'"
      [class.text-neutral-500]="activeTab() !== 'sessions'"
      [class.hover:text-neutral-700]="activeTab() !== 'sessions'"
      i18n>
      Sessions
    </button>
    <button
      (click)="switchTab('monitor')"
      class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
      [class.border-blue-600]="activeTab() === 'monitor'"
      [class.text-blue-600]="activeTab() === 'monitor'"
      [class.border-transparent]="activeTab() !== 'monitor'"
      [class.text-neutral-500]="activeTab() !== 'monitor'"
      [class.hover:text-neutral-700]="activeTab() !== 'monitor'"
      i18n>
      Monitor
    </button>
  </div>

  @switch (activeTab()) {
    @case ('sessions') {
      <!-- Filters -->
      <app-session-filters (filtersChanged)="onFiltersChanged($event)"></app-session-filters>

      <!-- Sessions Table -->
      <div class="courses-list">
        <div class="card rounded-lg border border-neutral-100">
          <div class="card-subheader flex items-center justify-between px-6 py-5">
            <div class="flex items-center gap-1">
              <h6 i18n>Sessions</h6>
              <div class="tag tag-sm text-slate-600">{{ totalSessions() }} <span i18n>Sessions</span></div>
            </div>
          </div>
          <div class="card-body">
            <div class="courses-list relative">
              @if (isLoading()) {
                <div class="flex items-center justify-center w-full h-full left-0 right-0 absolute bg-[rgba(255,255,255,0.5)] z-20"
                  [thkLibProcessing]="true"></div>
              }
              @if (sessions().length === 0 && !isLoading()) {
                <span class="text-center text-2xl my-11 block" i18n>No sessions found</span>
              } @else {
                <div class="datagrid-table relative overflow-x-auto">
                  <table class="w-full text-left">
                    <thead class="heading">
                      <tr>
                        <th scope="col"><div class="col-name" i18n>ID</div></th>
                        <th scope="col"><div class="col-name" i18n>Course</div></th>
                        <th scope="col"><div class="col-name" i18n>Series</div></th>
                        <th scope="col"><div class="col-name" i18n>Type</div></th>
                        <th scope="col"><div class="col-name" i18n>Instructor</div></th>
                        <th scope="col"><div class="col-name" i18n>Date/Time</div></th>
                        <th scope="col"><div class="col-name" i18n>Duration</div></th>
                        <th scope="col"><div class="col-name" i18n>Status</div></th>
                        <th scope="col"><div class="col-name" i18n>Live</div></th>
                        <th scope="col"><div class="col-name" i18n>Capacity</div></th>
                        <th scope="col"><div class="col-name" i18n>Attendees</div></th>
                        <th scope="col"><div class="col-name" i18n>Instructor Joined</div></th>
                        <th scope="col"><div class="col-name" i18n>Inst. Phone</div></th>
                        <th scope="col"><div class="col-name" i18n>Video Duration</div></th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (session of sessions(); track session.id) {
                        <tr (click)="openDrawer(session)" class="hover:cursor-pointer hover:bg-neutral-50">
                          <td>
                            <a (click)="viewSession($event, session)" class="text-blue-600 underline cursor-pointer">#{{ session.id }}</a>
                          </td>
                          <td>
                            <div class="max-w-[200px]">
                              <span class="line-clamp-2">{{ session.course_title }}</span>
                            </div>
                          </td>
                          <td>
                            <span class="whitespace-nowrap text-sm text-neutral-600">{{ session.series_name }}</span>
                          </td>
                          <td>
                            @if (session.session_type) {
                            <span class="whitespace-nowrap text-xs px-2 py-0.5 rounded-full"
                              [class.bg-slate-100]="session.session_type === 'single_session'"
                              [class.bg-blue-100]="session.session_type === 'multiple_session'"
                              [class.bg-violet-100]="session.session_type === 'freestyle'"
                            >@switch (session.session_type) {
                              @case ('freestyle') { Freestyle }
                              @case ('single_session') { Single }
                              @case ('multiple_session') { Multi }
                              @default { {{ session.session_type }} }
                            }</span>
                            } @else {
                            <span class="text-neutral-400">-</span>
                            }
                          </td>
                          <td>
                            <span class="whitespace-nowrap">{{ session.instructor_name }}</span>
                          </td>
                          <td>
                            <span class="whitespace-nowrap">{{ session.start_datetime | date:'MMM d, y, h:mm a' }}</span>
                          </td>
                          <td>
                            <span>{{ session.duration }} min</span>
                          </td>
                          <td>
                            <div class="tag tag-sm rounded-lg" [class]="getTemporalStatusClass(session.temporal_status)">
                              {{ getTemporalStatusLabel(session.temporal_status) }}
                            </div>
                          </td>
                          <td>
                            @if (session.is_video_active) {
                              <span class="inline-block w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" title="Live"></span>
                            } @else {
                              <span class="inline-block w-2.5 h-2.5 bg-neutral-300 rounded-full" title="Not Live"></span>
                            }
                          </td>
                          <td>
                            <span>{{ session.enrolled_count }}{{ session.max_participants ? ' / ' + session.max_participants : '' }}</span>
                          </td>
                          <td>
                            <span>{{ session.actual_attendee_count ?? '-' }}</span>
                          </td>
                          <td>
                            @if (session.instructor_joined_at) {
                              @if (session.instructor_late) {
                                <span class="text-amber-600" i18n>Late</span>
                              } @else {
                                <span class="text-green-600" i18n>Yes</span>
                              }
                            } @else {
                              @if (session.temporal_status === 'in_progress') {
                                <span class="text-red-600" i18n>No</span>
                              } @else {
                                <span class="text-neutral-400">-</span>
                              }
                            }
                          </td>
                          <td>
                            @if (session.instructor_phone) {
                              <a [href]="'tel:' + session.instructor_phone" (click)="$event.stopPropagation()" class="text-blue-600 text-sm underline">{{ session.instructor_phone }}</a>
                            } @else {
                              <span class="text-neutral-400">-</span>
                            }
                          </td>
                          <td>
                            <span>{{ formatDuration(session.video_duration_minutes) }}</span>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
              <!-- Pagination -->
              <div class="z-30 relative">
                <app-common-pagination
                  (paginatorData)="paginationAction($event)"
                  (pageSize)="pageSizeAction($event)"
                  [config]="paginationConfig"
                  [type]="'dynamic'">
                </app-common-pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    @case ('monitor') {
      <app-admin-monitor></app-admin-monitor>
    }
  }
</div>

<!-- Session Drawer -->
<app-session-drawer #sessionDrawer></app-session-drawer>
